import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import APP_CONFIG, { DASHBOARD_URL } from "../../../constants/app";

/* ─────────────────────────────────────────────
   Scoped styles — Fintech Animated Theme (OTP)
   Matches SignIn page exactly
───────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  /* ── Reset ── */
  .ft-page *, .ft-page *::before, .ft-page *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* ── Root ── */
  .ft-page {
    min-height: 100vh;
    display: flex;
    font-family: 'DM Sans', sans-serif;
    background: #060b18;
    overflow: hidden;
    position: relative;
  }

  /* ── Canvas background ── */
  .ft-canvas {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }

  /* ── Floating particles ── */
  .ft-particles {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    overflow: hidden;
  }
  .ft-particle {
    position: absolute;
    border-radius: 50%;
    opacity: 0;
    animation: ft-particle-float linear infinite;
  }
  @keyframes ft-particle-float {
    0%   { opacity: 0;   transform: translateY(100vh) scale(0); }
    10%  { opacity: 0.6; }
    90%  { opacity: 0.3; }
    100% { opacity: 0;   transform: translateY(-10vh) scale(1); }
  }

  /* ── Grid overlay ── */
  .ft-grid {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    background-image:
      linear-gradient(rgba(0,212,170,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,212,170,0.04) 1px, transparent 1px);
    background-size: 48px 48px;
  }

  /* ── Orbs ── */
  .ft-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 1;
    animation: ft-orb-drift ease-in-out infinite alternate;
  }
  .ft-orb-1 {
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(0,212,170,0.12) 0%, transparent 70%);
    top: -100px; left: -100px;
    animation-duration: 10s;
  }
  .ft-orb-2 {
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%);
    bottom: -80px; right: 300px;
    animation-duration: 14s;
    animation-delay: -3s;
  }
  .ft-orb-3 {
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%);
    top: 40%; right: -60px;
    animation-duration: 9s;
    animation-delay: -6s;
  }
  @keyframes ft-orb-drift {
    from { transform: translate(0, 0) scale(1); }
    to   { transform: translate(20px, 30px) scale(1.08); }
  }

  /* ── Ticker strip ── */
  .ft-ticker-wrap {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 2;
    height: 32px;
    background: rgba(0,212,170,0.05);
    border-top: 1px solid rgba(0,212,170,0.12);
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  .ft-ticker {
    display: flex;
    gap: 0;
    animation: ft-ticker-scroll 28s linear infinite;
    white-space: nowrap;
  }
  @keyframes ft-ticker-scroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  .ft-ticker-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 28px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: rgba(0,212,170,0.55);
    letter-spacing: 0.04em;
    border-right: 1px solid rgba(0,212,170,0.1);
  }
  .ft-ticker-item .up   { color: #00d4aa; }
  .ft-ticker-item .down { color: #f43f5e; }

  /* ── Left panel ── */
  .ft-left {
    flex: 1;
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 60px 48px;
    animation: ft-fade-in 0.8s ease both;
  }
  @keyframes ft-fade-in {
    from { opacity: 0; transform: translateX(-24px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ft-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 64px;
  }
  .ft-logo img {
    max-height: 36px;
    object-fit: contain;
  }

  .ft-headline {
    margin-bottom: 24px;
  }
  .ft-headline-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: #00d4aa;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 16px;
    padding: 4px 10px;
    border: 1px solid rgba(0,212,170,0.3);
    border-radius: 4px;
    background: rgba(0,212,170,0.06);
  }
  .ft-headline-tag::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #00d4aa;
    box-shadow: 0 0 8px #00d4aa;
    animation: ft-blink 1.8s ease-in-out infinite;
  }
  @keyframes ft-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
  .ft-headline h1 {
    font-size: clamp(2rem, 3.5vw, 2.8rem);
    font-weight: 700;
    color: #fff;
    line-height: 1.12;
    letter-spacing: -0.02em;
  }
  .ft-headline h1 .accent { color: #00d4aa; }

  .ft-desc {
    font-size: 14px;
    color: rgba(255,255,255,0.4);
    line-height: 1.7;
    max-width: 380px;
    margin-bottom: 48px;
  }

  /* ── OTP Steps (left panel) ── */
  .ft-steps {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 380px;
    margin-bottom: 48px;
  }
  .ft-step-item {
    display: flex;
    align-items: center;
    gap: 14px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px;
    padding: 14px 18px;
  }
  .ft-step-num {
    width: 28px; height: 28px;
    border-radius: 50%;
    background: rgba(0,212,170,0.15);
    border: 1px solid rgba(0,212,170,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    font-weight: 500;
    color: #00d4aa;
    flex-shrink: 0;
  }
  .ft-step-item.ft-step-done .ft-step-num {
    background: rgba(0,212,170,0.25);
    border-color: #00d4aa;
  }
  .ft-step-item.ft-step-active .ft-step-num {
    background: #00d4aa;
    color: #020c08;
    box-shadow: 0 0 12px rgba(0,212,170,0.4);
  }
  .ft-step-text {
    font-size: 13px;
    color: rgba(255,255,255,0.5);
    font-weight: 500;
  }
  .ft-step-item.ft-step-active .ft-step-text { color: rgba(255,255,255,0.85); }
  .ft-step-item.ft-step-done .ft-step-text   { color: rgba(0,212,170,0.7); }

  /* ── Shield visual ── */
  .ft-shield-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 140px;
    height: 140px;
    margin-bottom: 32px;
  }
  .ft-shield-bg {
    width: 120px; height: 120px;
    border-radius: 50%;
    background: rgba(0,212,170,0.06);
    border: 1.5px solid rgba(0,212,170,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3.5rem;
    filter: drop-shadow(0 0 20px rgba(0,212,170,0.3));
    animation: ft-float 3s ease-in-out infinite;
  }
  @keyframes ft-float {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-8px); }
  }
  .ft-shield-orbit {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1.5px dashed rgba(0,212,170,0.2);
    animation: ft-spin 10s linear infinite;
  }
  @keyframes ft-spin { to { transform: rotate(360deg); } }
  .ft-shield-dot {
    position: absolute;
    top: -5px; left: 50%;
    width: 10px; height: 10px;
    border-radius: 50%;
    background: #00d4aa;
    transform: translateX(-50%);
    box-shadow: 0 0 8px rgba(0,212,170,0.8);
  }

  /* ── Right panel (form) ── */
  .ft-right {
    width: 460px;
    flex-shrink: 0;
    position: relative;
    z-index: 3;
    background: rgba(10,17,30,0.92);
    border-left: 1px solid rgba(255,255,255,0.07);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 44px;
    animation: ft-slide-right 0.6s ease both;
    backdrop-filter: blur(20px);
  }
  @keyframes ft-slide-right {
    from { opacity: 0; transform: translateX(30px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ft-form-inner { width: 100%; }

  /* ── Form header ── */
  .ft-form-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 36px;
  }
  .ft-form-brand img { max-height: 32px; object-fit: contain; }

  .ft-form-hd { margin-bottom: 28px; }
  .ft-form-pre {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    color: #00d4aa;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ft-form-pre::before {
    content: '';
    display: block;
    width: 24px; height: 1px;
    background: #00d4aa;
  }
  .ft-form-title {
    font-size: 26px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 6px;
    letter-spacing: -0.02em;
  }
  .ft-form-sub {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
    font-weight: 400;
    line-height: 1.6;
    margin-bottom: 10px;
  }

  /* Phone badge */
  .ft-phone-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(0,212,170,0.08);
    border: 1px solid rgba(0,212,170,0.25);
    border-radius: 20px;
    padding: 5px 12px;
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    font-weight: 500;
    color: #00d4aa;
    letter-spacing: 0.05em;
  }

  /* ── Alert ── */
  .ft-alert {
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: ft-alert-in 0.3s ease both;
  }
  @keyframes ft-alert-in {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .ft-alert-danger {
    background: rgba(244,63,94,0.08);
    color: #fb7185;
    border: 1px solid rgba(244,63,94,0.25);
  }
  .ft-alert-success {
    background: rgba(0,212,170,0.08);
    color: #00d4aa;
    border: 1px solid rgba(0,212,170,0.25);
  }

  /* ── OTP input label ── */
  .ft-inputs-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255,255,255,0.45);
    margin-bottom: 14px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  /* ── OTP digit boxes ── */
  .ft-otp-row {
    display: flex;
    gap: 10px;
    margin-bottom: 16px;
  }
  .ft-digit {
    width: 52px;
    height: 56px;
    flex: 0 0 52px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    font-family: 'DM Mono', monospace;
    font-size: 1.3rem;
    font-weight: 500;
    color: #fff;
    background: rgba(255,255,255,0.04);
    text-align: center;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s, transform 0.15s;
    outline: none;
    caret-color: #00d4aa;
    -moz-appearance: textfield;
  }
  .ft-digit::-webkit-outer-spin-button,
  .ft-digit::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .ft-digit:focus {
    border-color: rgba(0,212,170,0.5);
    background: rgba(0,212,170,0.04);
    box-shadow: 0 0 0 3px rgba(0,212,170,0.1);
    transform: translateY(-2px);
  }
  .ft-digit-filled {
    border-color: rgba(0,212,170,0.4);
    background: rgba(0,212,170,0.06);
    color: #00d4aa;
  }
  .ft-digit-error {
    border-color: rgba(244,63,94,0.5) !important;
    box-shadow: 0 0 0 3px rgba(244,63,94,0.1) !important;
    color: #fb7185 !important;
    animation: ft-shake 0.35s ease;
  }
  @keyframes ft-shake {
    0%, 100% { transform: translateX(0); }
    20%       { transform: translateX(-5px); }
    60%       { transform: translateX(5px); }
  }

  /* ── Progress dots ── */
  .ft-progress {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 24px;
  }
  .ft-progress-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
    transition: background 0.2s, transform 0.2s;
  }
  .ft-progress-dot.active {
    background: #00d4aa;
    transform: scale(1.35);
    box-shadow: 0 0 6px rgba(0,212,170,0.5);
  }
  .ft-progress-dot.done { background: rgba(0,212,170,0.5); }

  /* ── Error msg ── */
  .ft-error-msg {
    font-size: 11px;
    color: #fb7185;
    font-weight: 500;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 4px;
    animation: ft-alert-in 0.2s ease both;
  }

  /* ── Resend row ── */
  .ft-resend-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 26px;
  }
  .ft-resend-label {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
    font-weight: 500;
  }
  .ft-timer-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    padding: 3px 10px;
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: rgba(255,255,255,0.5);
  }
  .ft-resend-btn {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #00d4aa;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 5px;
    opacity: 0.85;
    transition: opacity 0.15s;
  }
  .ft-resend-btn:hover:not(:disabled) { opacity: 1; text-decoration: underline; }
  .ft-resend-btn:disabled { color: rgba(255,255,255,0.25); cursor: not-allowed; opacity: 1; }
  .ft-resend-max {
    font-size: 11px;
    color: #fb7185;
    font-weight: 600;
  }

  /* ── Submit button ── */
  .ft-btn {
    width: 100%;
    padding: 13px 24px;
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    background: #00d4aa;
    color: #020c08;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 14px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    transition: transform 0.15s, box-shadow 0.15s, background 0.2s;
    position: relative;
    overflow: hidden;
  }
  .ft-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  .ft-btn:hover:not(:disabled)::before { transform: translateX(100%); }
  .ft-btn:hover:not(:disabled) {
    background: #00e8bb;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,212,170,0.3);
  }
  .ft-btn:active:not(:disabled) { transform: translateY(0); box-shadow: none; }
  .ft-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  /* ── Back button ── */
  .ft-back-btn {
    width: 100%;
    padding: 12px 24px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    background: rgba(255,255,255,0.03);
    color: rgba(255,255,255,0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .ft-back-btn:hover:not(:disabled) {
    border-color: rgba(0,212,170,0.35);
    color: #00d4aa;
    background: rgba(0,212,170,0.04);
  }
  .ft-back-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── Security badge ── */
  .ft-secure-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 24px;
    font-size: 10px;
    color: rgba(255,255,255,0.2);
    letter-spacing: 0.06em;
  }
  .ft-secure-badge span { font-family: 'DM Mono', monospace; }
  .ft-secure-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #00d4aa;
    box-shadow: 0 0 6px #00d4aa;
    animation: ft-blink 2.5s ease-in-out infinite;
  }

  /* ── Spinner ── */
  @keyframes ft-spin-btn {
    to { transform: rotate(360deg); }
  }
  .ft-spinner {
    display: inline-block;
    width: 1rem; height: 1rem;
    border: 2px solid rgba(2,12,8,0.3);
    border-top-color: #020c08;
    border-radius: 50%;
    animation: ft-spin-btn 0.7s linear infinite;
  }
  .ft-spinner-muted {
    display: inline-block;
    width: 10px; height: 10px;
    border: 2px solid rgba(0,212,170,0.3);
    border-top-color: #00d4aa;
    border-radius: 50%;
    animation: ft-spin-btn 0.7s linear infinite;
  }

  /* ── Responsive ── */
  @media (max-width: 991px) {
    .ft-left { display: none; }
    .ft-right { width: 100%; border-left: none; }
  }
  @media (max-width: 480px) {
    .ft-right { padding: 36px 24px; }
    .ft-digit  { width: 44px; height: 50px; font-size: 1.1rem; }
  }
`;

/* ─────────────────────────────────────────────
   Animated canvas — identical to SignIn
───────────────────────────────────────────── */
function AnimatedCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let nodes = [];
    const NODE_COUNT = 40;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    const initNodes = () => {
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.1,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width)  n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,212,170,${n.opacity * 0.4})`;
        ctx.fill();
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,212,170,${0.07 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    resize(); initNodes(); draw();
    window.addEventListener("resize", () => { resize(); initNodes(); });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={canvasRef} className="ft-canvas" />;
}

/* ─────────────────────────────────────────────
   Particles — identical to SignIn
───────────────────────────────────────────── */
const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  size: `${Math.random() * 4 + 2}px`,
  delay: `${Math.random() * 20}s`,
  duration: `${Math.random() * 15 + 12}s`,
  color: i % 3 === 0
    ? "rgba(0,212,170,0.5)"
    : i % 3 === 1
    ? "rgba(59,130,246,0.4)"
    : "rgba(139,92,246,0.4)",
}));
function Particles() {
  return (
    <div className="ft-particles">
      {PARTICLES.map((p) => (
        <div key={p.id} className="ft-particle" style={{
          left: p.left, width: p.size, height: p.size,
          background: p.color, animationDelay: p.delay, animationDuration: p.duration,
        }} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Ticker — identical to SignIn
───────────────────────────────────────────── */
const TICKERS = [
  { sym: "BTC/USD", val: "67,420.50", chg: "+2.34%", up: true },
  { sym: "ETH/USD", val: "3,841.20",  chg: "+1.12%", up: true },
  { sym: "USDT",    val: "1.0002",    chg: "0.00%",  up: true },
  { sym: "BNB/USD", val: "598.30",    chg: "-0.87%", up: false },
  { sym: "SOL/USD", val: "178.45",    chg: "+3.56%", up: true },
  { sym: "XRP/USD", val: "0.6312",    chg: "-1.23%", up: false },
  { sym: "ADA/USD", val: "0.4523",    chg: "+0.94%", up: true },
  { sym: "DOGE",    val: "0.1842",    chg: "-2.11%", up: false },
];
function Ticker() {
  const items = [...TICKERS, ...TICKERS];
  return (
    <div className="ft-ticker-wrap" aria-hidden="true">
      <div className="ft-ticker">
        {items.map((t, i) => (
          <span key={i} className="ft-ticker-item">
            {t.sym}&nbsp;
            <span>{t.val}</span>&nbsp;
            <span className={t.up ? "up" : "down"}>{t.chg}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   OTPInput — individual digit boxes
───────────────────────────────────────────── */
const OTPInput = ({ length = 6, value = "", onChange, onComplete, disabled = false, autoFocus = true, error = false }) => {
  const [otpValues, setOtpValues] = useState(
    Array(length).fill("").map((_, i) => value[i] || "")
  );
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) inputRefs.current[0].focus();
  }, [autoFocus]);

  useEffect(() => {
    setOtpValues(Array(length).fill("").map((_, i) => value[i] || ""));
  }, [value, length]);

  const handleChange = (index, inputValue) => {
    if (!/^\d*$/.test(inputValue)) return;
    const newValues = [...otpValues];
    newValues[index] = inputValue;
    setOtpValues(newValues);
    const joined = newValues.join("");
    onChange(joined);
    if (inputValue && index < length - 1) inputRefs.current[index + 1]?.focus();
    if (joined.length === length && onComplete) onComplete(joined);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!otpValues[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newValues = [...otpValues];
        newValues[index] = "";
        setOtpValues(newValues);
        onChange(newValues.join(""));
      }
    }
    if (e.key === "ArrowLeft"  && index > 0)          inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < length - 1) inputRefs.current[index + 1]?.focus();
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, "").slice(0, length);
        const newValues = Array(length).fill("").map((_, i) => digits[i] || "");
        setOtpValues(newValues);
        onChange(newValues.join(""));
        const nextIdx = Math.min(digits.length, length - 1);
        inputRefs.current[nextIdx]?.focus();
        if (digits.length === length && onComplete) onComplete(digits);
      });
    }
  };

  return (
    <div className="ft-otp-row">
      {otpValues.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          className={[
            "ft-digit",
            error  ? "ft-digit-error"  : "",
            digit  ? "ft-digit-filled" : "",
          ].filter(Boolean).join(" ")}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          autoComplete="off"
        />
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
   OTPModal — main component
───────────────────────────────────────────── */
const OTPModal = ({
  phoneNumber,
  password,
  userToken,
  otpLength = 5,
  onSuccess,
  onError,
  onResend,
  onBack,
  verifyOTPService,
  resendWaitMinutes = 0.5,
  maxResendAttempts = 3,
  autoSubmitOnComplete = false,
  title = "Verify OTP",
  subtitle = "Enter the verification code sent to your registered phone number",
}) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  const navigate = useNavigate();
  const cooldownSeconds = Math.floor(resendWaitMinutes * 60);

  useEffect(() => {
    let t;
    if (resendCooldown > 0) t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const formatTime = (s) =>
    `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const canResend = resendAttempts < maxResendAttempts && resendCooldown === 0 && !resendLoading;

  const maskPhone = (phone) => {
    if (!phone) return "";
    const s = phone.toString();
    if (s.length <= 4) return s;
    return s.slice(0, 2) + "•".repeat(s.length - 4) + s.slice(-2);
  };

  const handleOTPChange = (val) => {
    setOtp(val);
    if (error) setError("");
    if (resendSuccess) setResendSuccess(false);
  };

  const handleOTPComplete = (val) => {
    setOtp(val);
    if (autoSubmitOnComplete) handleSubmit(null, val);
  };

  const handleSubmit = async (e, otpValue = otp) => {
    if (e) e.preventDefault();
    if (otpValue.length !== otpLength) {
      setError(`Please enter the complete ${otpLength}-digit code`);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("phone", phoneNumber);
      formData.append("otp", otpValue);
      formData.append("password", password);
      formData.append("fcm_token", localStorage.getItem("fcmToken"));
      const result = await verifyOTPService(formData);
      if (result?.status) {
        onSuccess && onSuccess(result);
        localStorage.setItem("userData", JSON.stringify(result.data));
        navigate(DASHBOARD_URL[result.data.role], { replace: true });
      } else {
        setError(result.data?.error || "Invalid OTP. Please try again.");
        setOtp("");
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setError("An unexpected error occurred. Please try again.");
      onError && onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setResendLoading(true);
    setError("");
    setResendSuccess(false);
    try {
      const result = await onResend();
      if (result?.status) {
        setResendCooldown(cooldownSeconds);
        setResendAttempts((p) => p + 1);
        setOtp("");
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 4000);
      } else {
        setError(result?.data?.error || "Failed to resend OTP. Please try again.");
      }
    } catch {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const filled = otp.length;

  return (
    <>
      <style>{styles}</style>

      <div className="ft-page">
        {/* Animated background */}
        <AnimatedCanvas />
        <Particles />
        <div className="ft-grid" aria-hidden="true" />
        <div className="ft-orb ft-orb-1" aria-hidden="true" />
        <div className="ft-orb ft-orb-2" aria-hidden="true" />
        <div className="ft-orb ft-orb-3" aria-hidden="true" />

        {/* ── Left panel ── */}
        <div className="ft-left">
          <div className="ft-logo">
            <img src={APP_CONFIG.ORGANISATION_LOGO} alt="Logo" />
          </div>

          {/* Animated shield */}
          <div className="ft-shield-wrap">
            <div className="ft-shield-orbit">
              <div className="ft-shield-dot" />
            </div>
            <div className="ft-shield-bg" aria-hidden="true">🛡️</div>
          </div>

          <div className="ft-headline">
            <div className="ft-headline-tag">OTP Verification</div>
            <h1>
              Two-step <span className="accent">secure</span>
              <br />access control
            </h1>
          </div>

          <p className="ft-desc">
            Your account is protected by an extra layer of OTP-based security.
            Enter the code sent to your registered phone to continue.
          </p>

          <div className="ft-steps">
            <div className="ft-step-item ft-step-done">
              <div className="ft-step-num">✓</div>
              <span className="ft-step-text">Phone &amp; password verified</span>
            </div>
            <div className="ft-step-item ft-step-active">
              <div className="ft-step-num">2</div>
              <span className="ft-step-text">Verify the OTP sent to your phone</span>
            </div>
            <div className="ft-step-item">
              <div className="ft-step-num">3</div>
              <span className="ft-step-text">Access your dashboard securely</span>
            </div>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="ft-right">
          <div className="ft-form-inner">
            <div className="ft-form-brand">
              <img src={APP_CONFIG.ORGANISATION_LOGO} alt="Logo" />
            </div>

            <div className="ft-form-hd">
              <p className="ft-form-pre">Security Check</p>
              <h2 className="ft-form-title">{title}</h2>
              <p className="ft-form-sub">{subtitle}</p>
              {phoneNumber && (
                <div className="ft-phone-badge">
                  📞 {maskPhone(phoneNumber)}
                </div>
              )}
            </div>

            {/* Alerts */}
            {error && (
              <div className="ft-alert ft-alert-danger">
                <span>⚠️</span>{error}
              </div>
            )}
            {resendSuccess && !error && (
              <div className="ft-alert ft-alert-success">
                <span>✅</span>OTP has been resent successfully!
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* OTP label */}
              <label className="ft-inputs-label">
                Enter {otpLength}-digit verification code
              </label>

              {/* Digit inputs */}
              <OTPInput
                length={otpLength}
                value={otp}
                onChange={handleOTPChange}
                onComplete={handleOTPComplete}
                disabled={loading}
                error={!!error}
              />

              {/* Progress dots */}
              <div className="ft-progress">
                {Array.from({ length: otpLength }).map((_, i) => (
                  <div
                    key={i}
                    className={[
                      "ft-progress-dot",
                      i < filled  ? "done"   : "",
                      i === filled ? "active" : "",
                    ].filter(Boolean).join(" ")}
                  />
                ))}
              </div>

              {/* Resend row */}
              <div className="ft-resend-row">
                <span className="ft-resend-label">Didn't receive a code?</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {resendCooldown > 0 && (
                    <span className="ft-timer-pill">⏱ {formatTime(resendCooldown)}</span>
                  )}
                  {resendAttempts < maxResendAttempts ? (
                    <button
                      type="button"
                      className="ft-resend-btn"
                      onClick={handleResend}
                      disabled={!canResend}
                    >
                      {resendLoading ? (
                        <><span className="ft-spinner-muted" /> Sending…</>
                      ) : (
                        <>
                          Resend OTP
                          {resendAttempts > 0 && (
                            <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.3)", fontWeight: 600 }}>
                              ({resendAttempts}/{maxResendAttempts})
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  ) : (
                    <span className="ft-resend-max">Max attempts reached</span>
                  )}
                </div>
              </div>

              {/* Verify button */}
              <button
                className="ft-btn"
                type="submit"
                disabled={loading || otp.length !== otpLength}
              >
                {loading ? (
                  <><span className="ft-spinner" /> Verifying…</>
                ) : (
                  <>Verify &amp; Continue →</>
                )}
              </button>

              {/* Back button */}
              {onBack && (
                <button
                  type="button"
                  className="ft-back-btn"
                  onClick={onBack}
                  disabled={loading}
                >
                  ← Back to Login
                </button>
              )}
            </form>

            <div className="ft-secure-badge">
              <div className="ft-secure-dot" aria-hidden="true" />
              <span>256-BIT SSL · OTP SECURED · PCI DSS</span>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <Ticker />
      </div>
    </>
  );
};

export default OTPModal;